import type { PropType } from 'vue';
import type { BlockChildRenderContext } from '../../utils/block';
import { defineComponent, h, inject, provide } from 'vue';
import { blockChildKey } from '#layers/block-engine/utils/block/api/context';
import { isBlockSerializedChild } from '#layers/block-engine/utils/block/editor';
import { useBlockRenderProps } from '#layers/block-engine/utils/block/props';
import { blockChildRenderContextKey } from '../../utils/block';

export default defineComponent({
	props: {
		childId: {
			type: String,
			required: true,
		},
		context: {
			type: Object as PropType<BlockChildRenderContext>,
		},
	},
	setup(props, { slots }) {
		const childRenderOptions = props.context ?? inject(blockChildRenderContextKey);

		if (!childRenderOptions) {
			throw new Error('BlockCustomChildRenderContext not provided');
		}

		const getChild = () => childRenderOptions.block.children?.[props.childId];

		provide(blockChildKey, () => {
			const child = getChild();

			if (!child || !isBlockSerializedChild(child)) {
				throw new Error(`Child with id ${props.childId} is unavailable`);
			}

			return child;
		});

		const { getProps } = useBlockRenderProps({
			variables: childRenderOptions.variables,
			variableIdToName: childRenderOptions.variableIdToName,
			getRootContext: childRenderOptions.getRootContext,
			emit: childRenderOptions.emit,
			unwrapBlockEvents: childRenderOptions.unwrapBlockEvents,
		});

		return () => {
			const child = getChild();

			if (!child) {
				return h('div', { style: { color: 'red' } }, `Child with id ${props.childId} not found in block ${childRenderOptions.block.id}`);
			}

			if (!isBlockSerializedChild(child)) {
				return h('div', { style: { color: 'red' } }, `Child with id ${props.childId} is not a serialized child`);
			}

			const blockComponent = childRenderOptions.blockRenderComponents[child.blockId];

			if (!blockComponent) {
				return h('div', { style: { color: 'red' } }, `Block component with id ${child.blockId} not found`);
			}

			const childBlock = childRenderOptions.childrenBlocks[child.blockId];
			const childProps = getProps({
				child,
				childBlock,
			});

			return h(blockComponent, childProps, slots);
		};
	},
});
